import { getMaxQuantityString, getQuantityMapFromStorage } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withCart } from '../../utils/CartContext';
import { useAddProductToCart, useRemoveProductFromCart } from '../../utils/hooks/useCartActionButtons';
import _ from '../../utils/LodashImports';
import { noop } from '../../utils/noop';
import { getQuantityUnit } from '../../utils/store.helpers';
import { withUser } from '../../utils/UserContext';
import { cartItemControlsStyle } from './CartItemControls.style';

const CartItemControls = memo(({ appCtx, cart, product }) => {
  const { addState, addToCart } = useAddProductToCart();
  const { removeState, removeFromCart } = useRemoveProductFromCart();

  const [hasReachedMaxQty, setHasReachedMaxQty] = useState(false);
  const { cartItems, limitAlerts } = cart;

  const { id: skuId } = product;
  const item = cartItems?.[0]?.skuId === skuId ? cartItems[0] : {};

  const { totalQuantity = 0, commerceItemId, cartLineId = '' } = item;
  const [inputVal, setInputVal] = useState(totalQuantity);
  const [cartLineIdValue, setCartLineIdValue] = useState(cartLineId);
  const [limit, setLimit] = useState(product?.measurement?.maxUnits || 999);
  const minSaleQty = product?.measurement?.skuminsaleqty || 1;
  const unit = product?.measurement?.unit || 'UN';

  const [isLoading, setIsLoading] = useState(false);

  const { setBusy, store } = appCtx;
  const timeputForLoginModalOn401 = _.get(appCtx, 'siteConfig.textDictionary.TIMEOUT_FOR_LOGIN_MODAL', 1000);

  useEffect(() => {
    if (addState.loading || removeState.loading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [addState.loading, removeState.loading]);

  useEffect(() => {
    setBusy(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (addState.error === 'Unauthorized' || removeState.error === 'Unauthorized') {
      setTimeout(() => {
        cart.setShowCart(false);
        cart.setShowLoginForm(true);
      }, timeputForLoginModalOn401);
    }
  }, [addState, removeState]);

  useEffect(() => {
    const quantitiesMap = getQuantityMapFromStorage();
    if (cart?.cartItems?.[0]?.skuId === skuId) {
      const skuCartItems = cart.cartItems[0];
      const skuQtyLimits = skuCartItems.qtyLimits;
      const skuTotalQty = skuCartItems.totalQuantity;
      setInputVal(skuTotalQty);
      if (skuQtyLimits) {
        setLimit(skuQtyLimits);
      }
      setHasReachedMaxQty(skuTotalQty >= limit);
    } else if (quantitiesMap.has(skuId)) {
      const {
        quantity = totalQuantity,
        cartLineId: cookieCartLineId = cartLineId,
        quantityLimit,
      } = quantitiesMap.get(skuId);
      setInputVal(quantity);
      setCartLineIdValue(cookieCartLineId);
      setLimit(quantityLimit);
      setHasReachedMaxQty(quantity >= quantityLimit);
    } else {
      setInputVal(0);
      setCartLineIdValue('');
      setHasReachedMaxQty(false);
    }
  }, [cart, skuId]);

  useEffect(() => {
    if (limitAlerts && (limitAlerts[cartLineId] || limitAlerts.basket)) {
      if (limitAlerts[cartLineId]) {
        setHasReachedMaxQty(true);
      }
    }
  }, [limitAlerts]);

  const handleRemoveItem = async () => {
    const products = [
      {
        cartLineId: cartLineId || cartLineIdValue,
        totalQuantity: Number(inputVal),
        commerceItemId,
        quantity: 1,
      },
    ];
    await removeFromCart(products);
  };

  const addItem = () => {
    return {
      hasVariations: false,
      productId: product.productId,
      quantity: 1,
      quantityUnit: getQuantityUnit({ store }),
      skuId: product.id,
      offeringId: product.offeringId,
      colorCode: '',
      colorName: '',
    };
  };

  const handleAddItem = async () => {
    if (!hasReachedMaxQty) {
      await addToCart(addItem(), product);
    }
  };

  const roundOffPrice = (value, precision = 2) => {
    const power = 10 ** (precision || 0);
    return Math.round(value * power) / power;
  };
  const displayUnit = () => {
    return `${roundOffPrice(inputVal * minSaleQty)} ${unit}`.toLowerCase();
  };

  const preventParentEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <div
        key={`add-to-cart-button-${product.offeringId}`}
        id={`add-to-cart-button-${product.offeringId}`}
        role="button"
        onKeyDown={noop}
        className="add-to-cart"
        onClick={(e) => preventParentEvents(e)}
        tabIndex={0}
      >
        <button
          id="remove-from-cart-button"
          className={`${!inputVal ? 'hiddenEl' : ''} remove-from-cart-button`}
          type="button"
          onClick={handleRemoveItem}
        >
          {inputVal > 1 ? <span className="remove-from-cart-minus-icon" /> : <span className="remove-from-cart-icon" />}
        </button>
        <div className={`${!inputVal ? 'hiddenEl' : ''} count-box`}>{displayUnit()}</div>
        <button
          type="button"
          className={`add-to-cart-button ${hasReachedMaxQty ? 'disabled' : ''}`}
          onClick={handleAddItem}
        >
          <span className="add-to-cart-icon" />
        </button>
      </div>
      <div className="quantity-display">
        {hasReachedMaxQty ? (
          <div className="max-qnty">{getMaxQuantityString({ maxUnits: limit * minSaleQty, unit })}</div>
        ) : (
          <div className="empty-quantity-display-space" />
        )}
      </div>
      <style jsx>{cartItemControlsStyle}</style>
    </>
  );
});

CartItemControls.propTypes = {
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  cart: PropTypes.object,
};

CartItemControls.defaultProps = {
  cart: {},
};

export { CartItemControls };
export default withApplicationContext(withCart(withUser(CartItemControls)));
